import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey } from '@mui/material/colors';
import AddIcon from '@mui/icons-material/Add';
import DoubleArrow from '@mui/icons-material/DoubleArrow';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import LaunchDarklyContext from '../../../../shared-components/contexts/LaunchDarklyContext';
import NotationResponseDialogV2 from '../../../../shared-components/Notation/NotationResponseDialogV2';
import { IPagedRequest } from '../../../../shared-interfaces/IPagedRequest';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import { INotationComment } from '../../../../shared-interfaces/Notation/INotationComment';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../shared-interfaces/User/ICoreUser';
import NotationService, {
  INotationCommentRequest,
} from '../../../../shared-services/Notation/NotationService';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { useNavigate } from 'react-router';

export interface INotationDetails {
  open: boolean;
  notation: INotation;
  setAddResponseTo: (x: INotation) => void;
  isOpen: boolean;
  setIsOpen: (x: boolean) => void;
  setAssignedTo: (u: ICoreUser) => void;
  assignedTo?: ICoreUser;
  onUpdate: (n: number) => void;
}

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    padding: '4px 0',
  },
  caption: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  detailContainer: {
    '& p': {
      margin: 0,
    },
    marginBottom: '8px',
  },
  greyIcon: {
    color: grey[500],
  },
  heading: {
    fontWeight: 600,
  },
  listItem: {
    paddingLeft: 0,
    borderBottom: '1px solid #dcdcdc',
  },
  listItemIcon: {
    minWidth: '38px',
  },
  responses: {
    marginTop: '8px',
  },
  responseButton: {
    marginRight: '8px',
  },
}));

function audienceMapper(audienceType: string) {
  switch (audienceType) {
    case 'All':
      return 'Professional Staff';
    case 'FullNetwork':
      return 'Full Network';
    case 'Private':
      return 'Private';
    case 'SpecificNetworkMembers':
      return 'Specific Network Members';
    default:
      return audienceType;
  }
}

const ResponseItemSkeleton = () => {
  const classes = useStyles();
  return (
    <ListItem alignItems="flex-start" className={classes.listItem}>
      <ListItemIcon className={classes.listItemIcon}>
        <Skeleton variant="rectangular" height="24px" width="24px" />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={<Skeleton variant="text" width="270px" height="15px" />}
        secondary={
          <div>
            <Skeleton variant="text" width="150px" height="15px" />
            <Skeleton variant="text" width="400px" height="15px" />
            <Skeleton variant="text" width="300px" height="15px" />
          </div>
        }
      />
    </ListItem>
  );
};

const NotationDetails = (props: INotationDetails) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const isLessThanSm = useMediaQuery(theme.breakpoints.down('md'));

  const [isLoading, setIsLoading] = useState(true);

  const [openResponseDialog, setOpenResponseDialog] = useState(false);
  const [openLowerDialog, setOpenLowerDialog] = useState(false);

  const [totalComments, setTotalComments] = useState(0);
  const [comments, setComments] = useState<INotationComment[]>([]);
  const [additionalAudience, setAdditionalAudience] = useState<ICoreUser[]>([]);

  const AdditionalAudienceList = () => {
    const mappedNames = additionalAudience.map(u => {
      return GetCoreUserFormattedName(u, false);
    });

    return (
      <>
        {additionalAudience.length > 3 ? (
          <>
            {mappedNames.slice(0, 3).join(', ')}{' '}
            <Tooltip title={mappedNames.join(', ')} arrow>
              <span>+ {mappedNames.length - 3} more</span>
            </Tooltip>
          </>
        ) : (
          <>{mappedNames.join(', ')}</>
        )}
      </>
    );
  };

  useEffect(() => {
    if (props.open && comments.length === 0) {
      NotationService.getComments({
        page: 1,
        pageSize: 3,
        searchParameters: {
          successNotificationId: props.notation.id,
        },
      } as IPagedRequest<INotationCommentRequest>)
        .then(c => {
          setComments(c.items);
          setTotalComments(c.totalItems);
        })
        .finally(() => {
          setIsLoading(false);
        });

      var audience = props.notation.advisorConnections
        .filter(x => x.connectionType === 'AdditionalAudienceMember')
        .map(x => x.account);

      setAdditionalAudience(audience);
    }
  }, [props.open]);

  const handleViewNotationDetails = () => {
    try {
      navigate(
        `/${props.notation.notationType.toLowerCase()}/${
          props.notation.id
        }/details/`,
        { state: { notation: props.notation } },
      );
    } catch {
      navigate(
        `/${props.notation.notationType.toLowerCase()}/${
          props.notation.id
        }/details/`,
      );
    }
  };

  return (
    <div style={{ padding: `${isLessThanSm ? '0 4px 0 94px' : '0 94px'}` }}>
      <div>
        <Typography component="h4" className={classes.heading}>
          Details:
        </Typography>
        <Box
          display={isLessThanSm ? 'unset' : 'flex'}
          flexDirection="row"
          p={0}
        >
          <Box>
            <div>
              <Typography
                variant="caption"
                className={classes.caption}
                data-clarity-mask="true"
              >
                Audience: {audienceMapper(props.notation.audienceType)}
                {props.notation.canStudentView && ', Visible to Student'}
              </Typography>
            </div>
          </Box>
          <Box style={{ marginLeft: `${isLessThanSm ? '0px' : '15px'}` }}>
            {additionalAudience.length > 0 && (
              <div>
                <Typography
                  variant="caption"
                  className={classes.caption}
                  data-clarity-mask="true"
                >
                  Additional Audience: <AdditionalAudienceList />
                </Typography>
              </div>
            )}
            {props.notation.incidentDateTime && (
              <div>
                <Typography
                  variant="caption"
                  className={classes.caption}
                  data-clarity-mask="true"
                >
                  Incident Date:{' '}
                  {`${new Date(
                    props.notation.incidentDateTime,
                  ).toLocaleDateString()} at ${new Date(
                    props.notation.incidentDateTime,
                  ).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`}
                </Typography>
              </div>
            )}
          </Box>
        </Box>
      </div>
      {props.notation.notationType !== NotationType.Encouragement ? (
        <div className={classes.responses}>
          <Typography component="h4" className={classes.heading}>
            Responses:
          </Typography>
          {isLessThanSm ? (
            <div className={classes.buttonContainer}>
              <div>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  className={classes.responseButton}
                  onClick={() => setOpenResponseDialog(true)}
                >
                  <AddIcon />
                  Add Response
                </Button>
              </div>
              {props.notation.notationType === NotationType.Alert &&
                props.isOpen && (
                  <div style={{ marginTop: '4px' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      className={classes.responseButton}
                      onClick={() => setOpenLowerDialog(true)}
                    >
                      <DoubleArrow style={{ transform: 'rotate(90deg)' }} />
                      Lower Alert
                    </Button>
                  </div>
                )}
            </div>
          ) : (
            <div className={classes.buttonContainer}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.responseButton}
                onClick={() => setOpenResponseDialog(true)}
              >
                <AddIcon />
                Add Response
              </Button>
              {props.notation.notationType === NotationType.Alert &&
                props.isOpen && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    className={classes.responseButton}
                    onClick={() => setOpenLowerDialog(true)}
                  >
                    <DoubleArrow style={{ transform: 'rotate(90deg)' }} />
                    Lower Alert
                  </Button>
                )}
            </div>
          )}
          <List>
            {isLoading ? (
              <>
                <ResponseItemSkeleton />
                <ResponseItemSkeleton />
                <ResponseItemSkeleton />
              </>
            ) : (
              <>
                {comments.length === 0 && (
                  <p className={classes.caption}>No responses</p>
                )}
                {comments.map(c => {
                  return (
                    <ListItem
                      key={c.id}
                      alignItems="flex-start"
                      className={classes.listItem}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        <QuestionAnswerIcon className={classes.greyIcon} />
                      </ListItemIcon>
                      <ListItemText
                        disableTypography
                        primary={
                          <div>
                            <span
                              style={{ fontWeight: 600 }}
                              data-clarity-mask="true"
                            >
                              {GetCoreUserFormattedName(c.createdBy, false)}
                            </span>
                            {' - '}
                            {c.createdOn.toLocaleDateString()} at{' '}
                            {c.createdOn.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        }
                        secondary={
                          <>
                            {c.action !== null && (
                              <div
                                style={{ fontStyle: 'italic' }}
                                data-clarity-mask="true"
                              >
                                Action: {c.action.name}
                                {c.methodOfContact !== null && (
                                  <span> - {c.methodOfContact.name}</span>
                                )}
                              </div>
                            )}
                            <div
                              className={classes.detailContainer}
                              dangerouslySetInnerHTML={{ __html: c.details }}
                              data-clarity-mask="true"
                            />
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </>
            )}
          </List>
          <Button
            size="small"
            variant="text"
            color="primary"
            aria-label={`View Notation ${props.notation.details} for ${
              (GetCoreUserFormattedName(props.notation.student), false)
            }`}
            onClick={() => {
              handleViewNotationDetails();
            }}
          >
            {totalComments > 3
              ? `View ${totalComments - 3} More Responses`
              : 'View Notation Details'}
          </Button>
        </div>
      ) : (
        <div className={classes.responses}>
          <Button
            size="small"
            variant="text"
            color="primary"
            aria-label={`View Notation ${props.notation.details} for ${
              (GetCoreUserFormattedName(props.notation.student), false)
            }`}
            onClick={() => {
              handleViewNotationDetails();
            }}
          >
            View Notation Details
          </Button>
        </div>
      )}
      <NotationResponseDialogV2
        open={openResponseDialog}
        close={() => setOpenResponseDialog(false)}
        notation={props.notation}
        assignable={true}
        additionalAudience={additionalAudience}
        usedToLower={false}
        title="Add Reponse"
        transition={isLessThanSm}
        onComplete={(success, comments, aa, at) => {
          if (success) {
            if (comments) setComments(comments.slice(0, 3));
            if (aa) setAdditionalAudience(aa);
            if (at) props.setAssignedTo(at);
            enqueueSnackbar('Response Added', { variant: 'success' });
          } else {
            enqueueSnackbar('Failed to Add Response', {
              variant: 'error',
            });
          }
        }}
      />
      <NotationResponseDialogV2
        open={openLowerDialog}
        close={() => setOpenLowerDialog(false)}
        notation={props.notation}
        assignable={false}
        usedToLower={true}
        title="Lower Alert"
        transition={isLessThanSm}
        onComplete={(success, comments) => {
          if (success) {
            if (comments) setComments(comments.slice(0, 3));
            props.setIsOpen(false);
            enqueueSnackbar('Alert Lowered', { variant: 'success' });
            props.onUpdate(-1);
          } else {
            enqueueSnackbar('Failed to Lower Alert', {
              variant: 'error',
            });
          }
        }}
      />
    </div>
  );
};

export default NotationDetails;
